import { forwardRef, useEffect, useRef } from "react";
import styles from "./ProductInfoTable.module.scss";
import { IReportProduct, ReportKeysEnum } from "@/entities/report/types";
import { useAppDispatch, useAppSelector } from "@/shared/hooks";
import clsx from "clsx";
import { setSelectedProductId } from "@/entities/report/slice";
import { getSortIndex } from "../../lib/buildSortIndexMap";

interface ProductInfoTableProps {
  oneParam: null | "remainsQuantity" | "totalQuantity";
  tableHeaders: (keyof typeof ReportKeysEnum)[];
  isFilialView?: boolean;
  showWideTable: boolean;
  showTotalLabel?: boolean;
  reportProductList: IReportProduct[];
  fixed?: boolean;
  isDraft?: boolean;
  sortIndexMap: Map<string, number>;
}

export const ProductInfoTable = forwardRef<
  HTMLDivElement,
  ProductInfoTableProps
>(
  (
    {
      reportProductList,
      oneParam = null,
      showWideTable = false,
      isFilialView = false,
      showTotalLabel = true,
      tableHeaders,
      fixed = false,
      isDraft = false,
      sortIndexMap,
    },
    ref,
  ) => {
    const dispatch = useAppDispatch();
    const ROW_HEIGHT = 50;
    const { selectedProductId } = useAppSelector((state) => state.reports);
    const selectedRowRef = useRef<HTMLTableRowElement>(null);

    const handleProductClick = (id: string) => {
      dispatch(setSelectedProductId(id));
    };

    useEffect(() => {
      if (selectedRowRef.current && ref && typeof ref !== "function") {
        const row = selectedRowRef.current;
        const container = ref.current;

        if (container) {
          const rowTop = row.offsetTop;
          const rowHeight = row.clientHeight;
          const containerHeight = container.clientHeight;

          const scrollTop = rowTop - containerHeight / 2 + rowHeight / 2;

          container.scrollTo({
            top: scrollTop,
            behavior: "smooth",
          });
        }
      }
    }, [selectedProductId, ref, isFilialView]);

    if (!tableHeaders.length) {
      return <></>;
    }
    const headerColSpan =
      !oneParam && tableHeaders.length ? tableHeaders.length : 1;

    return (
      <table className={clsx(styles.mainTable, { [styles.fixed]: fixed })}>
        <thead>
          {!isFilialView && (
            <tr className={styles.tableHeaderTopLine}>
              <th className={styles.totalRow} colSpan={headerColSpan}>
                {!showWideTable && showTotalLabel && "Итого"}
              </th>
            </tr>
          )}
          {showWideTable && (
            <tr>
              <th colSpan={headerColSpan}></th>
            </tr>
          )}
          <tr>
            {tableHeaders.map((el) => {
              return (
                <th className={styles.columnNameCell} key={el + el}>
                  {ReportKeysEnum[el]}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody
          style={{
            position: "relative",
            display: "block",
            height: reportProductList.length * ROW_HEIGHT,
          }}
        >
          {reportProductList.map((item) => {
            const index = getSortIndex(sortIndexMap, item.vendorId);
            return (
              <tr
                style={{
                  transform: `translateY(${index * ROW_HEIGHT}px)`,
                  position: "absolute",
                  height: ROW_HEIGHT,
                  width: "100%",
                }}
                key={item.vendorId}
                ref={
                  item.vendorId === selectedProductId
                    ? selectedRowRef
                    : undefined
                }
                className={clsx(
                  styles.row,
                  index % 2 === 0 ? styles.whiteBg : styles.grayBg,
                  {
                    [styles.selectedRow]:
                      (isDraft || isFilialView) &&
                      selectedProductId === item.vendorId,
                    [styles.unselectedRow]:
                      selectedProductId && selectedProductId !== item.vendorId,
                  },
                )}
              >
                {tableHeaders.map((el) => {
                  return (
                    <td
                      key={item.vendorId + el}
                      className={clsx({
                        [styles.canSelect]: isDraft && el === "code",
                        [styles.selectedCode]:
                          (isDraft || isFilialView) &&
                          el === "code" &&
                          selectedProductId === item.vendorId,
                      })}
                      onClick={() => {
                        if (el === "code" && isDraft)
                          handleProductClick(item.vendorId);
                      }}
                    >
                      {item[el] as keyof IReportProduct}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  },
);
